import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../../services/api';
import { errorMessage } from '../../hooks/useAsync';
import { Alert, Card, Checkbox, Field, Input, PageTitle, Select, Textarea } from '../../components/ui';
import { useRatedCurrencyCodes } from '../../hooks/useCurrencies';
import { EMPTY_TERMS, TermsForm } from './TermsForm';
import type { Terms } from '../../types/api';

export function OfferNew() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [key, setKey] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isPublic, setPublic] = useState(false);
  const [sort, setSort] = useState('10');
  const [currency, setCurrency] = useState('EUR');
  const currencies = useRatedCurrencyCodes();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const create = async (terms: Terms) => {
    setBusy(true);
    setError(null);
    try {
      const offer = await api.createOffer({ key: key.trim().toLowerCase(), name: name.trim(), description: description.trim(), public: isPublic, active: true, sort: parseInt(sort, 10) || 0, terms: { ...terms, currency } });
      navigate(`/offers/${offer.id}`, { replace: true });
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-3xl">
      <PageTitle title={t('offers.new')} subtitle={t('offers.newSubtitle')} />
      <div className="grid gap-4 lg:grid-cols-2">
        <Card title={t('offers.identity')}>
          <div className="flex flex-col gap-3">
            <Field label={t('offers.key')} hint={t('offers.keyHint')}>
              <Input required value={key} pattern="[a-z0-9][a-z0-9-]*" onChange={(e) => setKey(e.target.value)} data-testid="offer-key" />
            </Field>
            <Field label={t('offers.name')}>
              <Input required value={name} onChange={(e) => setName(e.target.value)} data-testid="offer-name" />
            </Field>
            <Field label={t('offers.currency')} hint={t('offers.currencyHint')}>
              <Select value={currency} onChange={(e) => setCurrency(e.target.value)} data-testid="offer-currency">
                {currencies.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label={t('offers.description')}>
              <Textarea value={description} onChange={(e) => setDescription(e.target.value)} />
            </Field>
            <Field label={t('offers.sort')}>
              <Input type="number" value={sort} onChange={(e) => setSort(e.target.value)} />
            </Field>
            <Checkbox label={t('offers.publicHint')} checked={isPublic} onChange={(e) => setPublic(e.target.checked)} />
          </div>
        </Card>
        <Card title={t('offers.terms')}>
          <TermsForm initial={EMPTY_TERMS} busy={busy} submitLabel={t('common.create')} onSubmit={(terms) => (key.trim() && name.trim() ? void create(terms) : setError(t('offers.keyAndNameRequired')))} notice={error ? <Alert>{error}</Alert> : undefined} />
        </Card>
      </div>
    </div>
  );
}
